import {
  Check,
  ChevronDown,
  Eye,
  Layers3,
  MessagesSquare,
  RotateCcw,
  ScrollText,
  Sparkles,
  UserRound,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { NavigationKey } from "../app/navigation";
import { roles, type DemoRole } from "../app/roles";
import type { ScenarioStage } from "../domain";
import {
  audienceDefinitions,
  depthDefinitions,
  presenterAudiences,
  presenterDepths,
  useAudience,
  useDepth,
} from "../presentation";
import { useScenario } from "../simulation/ScenarioContext";

type PresenterBarProps = {
  activeRole: DemoRole;
  onNavigate: (key: NavigationKey) => void;
  onRoleChange: (role: DemoRole) => void;
};

const depthOptions = presenterDepths.map((id) => depthDefinitions[id]);
const audienceOptions = presenterAudiences.map((id) => audienceDefinitions[id]);

const nextStepByStage: Record<ScenarioStage, string> = {
  calm: "Next · start the controlled outage",
  detected: "Next · open the incident and begin triage",
  investigating: "Next · link two evidence records",
  identified: "Next · compare response consequences",
  mitigating: "Next · verify the chosen response",
  monitoring: "Next · verify remaining service impact",
  recovered: "Next · review receipts and closure report",
};

export function PresenterBar({ activeRole, onNavigate, onRoleChange }: PresenterBarProps) {
  const [perspectiveOpen, setPerspectiveOpen] = useState(false);
  const [feedback, setFeedback] = useState("");
  const perspectiveRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { announcement, definition: activeDepth, depth, setDepth } = useDepth();
  const {
    announcement: audienceAnnouncement,
    audience,
    definition: activeAudience,
    setAudience,
  } = useAudience();
  const {
    guideStep,
    openGuide,
    receipts,
    resetScenario,
    stage,
  } = useScenario();
  const activeRoleDefinition = roles.find((role) => role.id === activeRole) ?? roles[1];
  useEffect(() => {
    if (!feedback) return;
    const timeout = window.setTimeout(() => setFeedback(""), 5000);
    return () => window.clearTimeout(timeout);
  }, [feedback]);

  useEffect(() => {
    if (!perspectiveOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (!perspectiveRef.current?.contains(event.target as Node)) {
        setPerspectiveOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setPerspectiveOpen(false);
      triggerRef.current?.focus();
    };

    document.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [perspectiveOpen]);

  const applyDepth = (nextDepth: (typeof presenterDepths)[number]) => {
    setDepth(nextDepth);
    setFeedback(`${depthDefinitions[nextDepth].label} view applied · ${depthDefinitions[nextDepth].effect}`);
  };

  const applyRole = (nextRole: DemoRole) => {
    const nextRoleDefinition = roles.find((role) => role.id === nextRole) ?? roles[1];
    onRoleChange(nextRole);
    setFeedback(`${nextRoleDefinition.label} permissions applied · ${nextRoleDefinition.description}`);
    setPerspectiveOpen(false);
    window.requestAnimationFrame(() => triggerRef.current?.focus());
  };

  const applyAudience = (nextAudience: (typeof presenterAudiences)[number]) => {
    setAudience(nextAudience);
    setFeedback(`${audienceDefinitions[nextAudience].label} language applied · ${audienceDefinitions[nextAudience].emphasis}`);
  };

  return (
    <section className="presenter-bar" aria-label="Demonstration controls">
      <span aria-live="polite" className="sr-only">{announcement} {audienceAnnouncement}</span>
      <div className="presenter-bar__story">
        <span className="presenter-bar__status" aria-hidden="true" />
        <div>
          <strong>North Academic response story</strong>
          <span>{nextStepByStage[stage]}</span>
        </div>
      </div>
      <div className="presenter-bar__actions">
        <button className="presenter-primary-action" onClick={openGuide} type="button">
          <Sparkles aria-hidden="true" size={16} />
          <span><strong>Guided flow</strong><small>Step {guideStep + 1} of 6</small></span>
        </button>
        <div className="perspective-control" ref={perspectiveRef}>
          <button
            aria-expanded={perspectiveOpen}
            aria-haspopup="dialog"
            className={perspectiveOpen ? "perspective-trigger is-open" : "perspective-trigger"}
            onClick={() => setPerspectiveOpen((open) => !open)}
            ref={triggerRef}
            type="button"
          >
            <UserRound aria-hidden="true" size={17} />
            <span>
              <small>Access and actions</small>
              <strong>{activeRoleDefinition.label}</strong>
            </span>
            <ChevronDown aria-hidden="true" size={16} />
          </button>
          {perspectiveOpen && (
            <section
              aria-label="Perspective settings"
              className="perspective-menu"
              role="dialog"
            >
              <header>
                <div>
                  <span className="eyebrow">Access permissions</span>
                  <strong>Choose the operator role. This changes authorization, not audience or page depth.</strong>
                </div>
                <button
                  aria-label="Close Perspective settings"
                  className="perspective-menu__close"
                  onClick={() => {
                    setPerspectiveOpen(false);
                    triggerRef.current?.focus();
                  }}
                  type="button"
                >
                  <X aria-hidden="true" size={18} />
                </button>
              </header>
              <fieldset>
                <legend><UserRound aria-hidden="true" size={15} /> Access and actions</legend>
                <div aria-label="Operator role" className="perspective-options" role="radiogroup">
                  {roles.map((role, index) => (
                    <button
                      aria-checked={activeRole === role.id}
                      aria-label={`${role.label}: ${role.description}`}
                      className={activeRole === role.id ? "perspective-option is-selected" : "perspective-option"}
                      key={role.id}
                      onClick={() => applyRole(role.id)}
                      onKeyDown={(event) => {
                        if (!["ArrowDown", "ArrowRight", "ArrowUp", "ArrowLeft"].includes(event.key)) return;
                        event.preventDefault();
                        const direction = event.key === "ArrowDown" || event.key === "ArrowRight" ? 1 : -1;
                        applyRole(roles[(index + direction + roles.length) % roles.length].id);
                      }}
                      role="radio"
                      type="button"
                    >
                      <span><strong>{role.label}</strong><small>{role.description}</small></span>
                      <span aria-hidden="true" className="perspective-option__check">
                        {activeRole === role.id && <Check size={17} />}
                      </span>
                    </button>
                  ))}
                </div>
              </fieldset>
              <p className="perspective-menu__boundary">
                <Eye aria-hidden="true" size={15} />
                Role changes permissions. Audience changes explanation language. Depth changes visible hierarchy and
                detail. Audience and depth never reveal restricted data or change the underlying record.
              </p>
            </section>
          )}
        </div>
        <button className="text-button" onClick={() => onNavigate("incidents")} type="button">
          <ScrollText aria-hidden="true" size={16} /> Receipts <span className="count-badge">{receipts.length}</span>
        </button>
        <button className="text-button text-button--quiet" onClick={resetScenario} type="button">
          <RotateCcw aria-hidden="true" size={15} /> Reset
        </button>
      </div>
      <div className="perspective-quick-controls" aria-label="Audience and depth controls">
        <fieldset className="perspective-toggle-group perspective-toggle-group--audience">
          <legend><MessagesSquare aria-hidden="true" size={16} /> Audience</legend>
          <div aria-label="Explanation audience" role="radiogroup">
            {audienceOptions.map((option) => (
              <button
                aria-checked={audience === option.id}
                aria-label={`${option.label}: ${option.description}`}
                className={audience === option.id ? "is-selected" : ""}
                key={option.id}
                onClick={() => applyAudience(option.id)}
                role="radio"
                type="button"
              >
                {option.label}
              </button>
            ))}
          </div>
        </fieldset>
        <fieldset className="perspective-toggle-group perspective-toggle-group--depth">
          <legend><Layers3 aria-hidden="true" size={16} /> Depth</legend>
          <div aria-label="Explanation depth" role="radiogroup">
            {depthOptions.map((option) => {
              const learningLevel = option.id === "overview"
                ? "Beginner"
                : option.id === "operations"
                  ? "Intermediate"
                  : "Expert";
              return (
                <button
                  aria-checked={depth === option.id}
                  aria-label={`${learningLevel} depth, ${option.label}: ${option.effect}`}
                  className={depth === option.id ? "is-selected" : ""}
                  key={option.id}
                  onClick={() => applyDepth(option.id)}
                  role="radio"
                  type="button"
                >
                  <strong>{learningLevel}</strong>
                  <small>{option.label}</small>
                </button>
              );
            })}
          </div>
        </fieldset>
      </div>
      {feedback && !perspectiveOpen && (
        <div className="perspective-feedback" role="status">
          <div className="perspective-feedback__heading">
            <Check aria-hidden="true" size={18} />
            <strong>Page perspective changed</strong>
          </div>
          <span>{feedback}</span>
          <dl className="perspective-feedback__summary" aria-label="Active perspective">
            <div>
              <dt>Access</dt>
              <dd>{activeRoleDefinition.label}</dd>
            </div>
            <div>
              <dt>Audience</dt>
              <dd>{activeAudience.label}</dd>
            </div>
            <div>
              <dt>Page</dt>
              <dd>{activeDepth.label}</dd>
            </div>
          </dl>
        </div>
      )}
    </section>
  );
}
